import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { Organization, UserOrganization } from '../types';
import { AuthContext } from './AuthContext';
import { setCurrentOrgId } from '../api/client';

export interface OrganizationContextType {
  currentOrg: Organization | null;
  organizations: UserOrganization[];
  switchOrg: (orgId: string) => void;
  isLoading: boolean;
}

export const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: React.ReactNode;
}

const CURRENT_ORG_KEY = 'current_org_id';

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Initialize organizations from user data
   */
  useEffect(() => {
    if (!authContext?.user) {
      // User not logged in, reset org state
      setCurrentOrg(null);
      setOrganizations([]);
      setCurrentOrgId(null);
      setIsLoading(false);
      localStorage.removeItem(CURRENT_ORG_KEY);
      return;
    }

    const userOrgs = authContext.user.organizations || [];
    setOrganizations(userOrgs);

    if (userOrgs.length === 0) {
      setCurrentOrg(null);
      setIsLoading(false);
      return;
    }

    // Try to restore previous org from localStorage
    const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY);
    const savedOrg = userOrgs.find(uo => uo.organization.id === savedOrgId);

    if (savedOrg) {
      setCurrentOrg(savedOrg.organization);
      setCurrentOrgId(savedOrg.organization.id);
    } else {
      // Default to first organization
      setCurrentOrg(userOrgs[0].organization);
      setCurrentOrgId(userOrgs[0].organization.id);
      localStorage.setItem(CURRENT_ORG_KEY, userOrgs[0].organization.id);
    }

    setIsLoading(false);
  }, [authContext?.user]);

  /**
   * Switch to a different organization
   */
  const switchOrg = useCallback((orgId: string) => {
    const userOrg = organizations.find(uo => uo.organization.id === orgId);

    if (!userOrg) {
      console.error(`Organization ${orgId} not found in user's organizations`);
      return;
    }

    setCurrentOrg(userOrg.organization);
    setCurrentOrgId(orgId);
    localStorage.setItem(CURRENT_ORG_KEY, orgId);

    // TODO: Clear any cached data when switching orgs
    // This will be implemented later when we add caching layer
  }, [organizations]);

  const value: OrganizationContextType = {
    currentOrg,
    organizations,
    switchOrg,
    isLoading,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
